import { useEffect, useState } from "react";
import { Bell, BellOff, X, ChevronDown, ChevronUp } from "lucide-react";

import {
  isNotificationAudioUnlocked,
  subscribeNotificationAudioStatus,
  unlockNotificationAudio,
} from "../../../../services/support/adminPushService";

/**
 * Bandeau affiché tant que le son des notifications n'est pas déverrouillé.
 *
 * Chrome exige une interaction utilisateur avant d'autoriser `audio.play()` :
 * sans ce clic, le son des notifications reste bloqué. Le bandeau disparaît
 * dès que l'audio est déverrouillé (par le clic sur le bouton ou par
 * n'importe quel autre geste capté par le hook).
 *
 * Réductible : une fois réduit, il devient une petite pastille cliquable qui
 * permet de le rouvrir.
 */
export default function NotificationAudioBanner(): JSX.Element | null {
  const [unlocked, setUnlocked] = useState(isNotificationAudioUnlocked());
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    setUnlocked(isNotificationAudioUnlocked());
    return subscribeNotificationAudioStatus(setUnlocked);
  }, []);

  if (unlocked) return null;

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => setCollapsed(false)}
        aria-label="Réouvrir le bandeau d'activation du son"
        style={{
          position: "fixed",
          right: "1rem",
          bottom: "1rem",
          zIndex: 9999,
          width: "2.75rem",
          height: "2.75rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#111",
          color: "#fff",
          border: "none",
          borderRadius: "999px",
          cursor: "pointer",
          boxShadow: "0 4px 14px rgba(0,0,0,.25)",
        }}
      >
        <BellOff size={18} />
      </button>
    );
  }

  return (
    <div
      role="region"
      aria-label="Activation du son des notifications"
      style={{
        position: "fixed",
        right: "1rem",
        bottom: "1rem",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        padding: "0.5rem 0.75rem",
        background: "#111",
        color: "#fff",
        borderRadius: "999px",
        boxShadow: "0 4px 14px rgba(0,0,0,.25)",
        fontSize: "0.85rem",
      }}
    >
      <Bell size={16} />
      <span>Activer le son des notifications</span>

      <button
        type="button"
        onClick={() => unlockNotificationAudio()}
        style={{
          marginLeft: "0.25rem",
          padding: "0.25rem 0.6rem",
          background: "#fff",
          color: "#111",
          border: "none",
          borderRadius: "999px",
          cursor: "pointer",
          fontSize: "0.8rem",
          fontWeight: 600,
        }}
      >
        Activer
      </button>

      <button
        type="button"
        onClick={() => setCollapsed(true)}
        aria-label="Réduire le bandeau"
        title="Réduire"
        style={{
          marginLeft: "0.25rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "1.5rem",
          height: "1.5rem",
          background: "transparent",
          color: "#fff",
          border: "none",
          borderRadius: "999px",
          cursor: "pointer",
          opacity: 0.7,
        }}
      >
        <ChevronDown size={16} />
      </button>

      <button
        type="button"
        onClick={() => setCollapsed(true)}
        aria-label="Fermer le bandeau"
        title="Fermer"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "1.5rem",
          height: "1.5rem",
          background: "transparent",
          color: "#fff",
          border: "none",
          borderRadius: "999px",
          cursor: "pointer",
          opacity: 0.7,
        }}
      >
        <X size={16} />
      </button>
    </div>
  );
}
